/* istanbul ignore file - better tested visually */

import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@partful/shared/ui/Button';
import {
  WizardPageContent,
  WizardPageFooter,
  WizardPageHeader,
  WizardPageLayout,
} from '@partful/shared/ui/layouts/WizardPageLayout';
import { ProgressBar } from '@partful/shared/ui/ProgressBar';
import { Typography } from '@partful/shared/ui/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { formatRemainingTime } from '../lib/formatRemainingTime';
import styles from './UploadingPage.module.scss';

type UploadingPageProps = {
  filename: string;
  progressPercentage?: number;
  remainingTime?: number;
  onCancel: () => void;
};

export function UploadingPage({
  filename,
  progressPercentage = 0,
  remainingTime = 0,
  onCancel,
}: Readonly<UploadingPageProps>) {
  const { t: i18n } = useTranslation();

  const progressStatus =
    progressPercentage < 100
      ? i18n('instructions:create.CAD.uploading')
      : i18n('instructions:create.CAD.uploadProcessing');

  const remainingTimeStatus = remainingTime
    ? ` - ${formatRemainingTime(remainingTime)} ${i18n('instructions:create.CAD.uploadTimeLeft')}`
    : '';

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <Typography appearance="heading5" as="h1">
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
        </Typography>
        <Typography appearance="heading6" as="h2">
          {i18n('instructions:create.CAD.uploadTitle')}
        </Typography>
        <Typography as="p">
          <Trans i18nKey="instructions:create.CAD.uploadDescription" />
        </Typography>
      </WizardPageHeader>
      <WizardPageContent className={styles.progress}>
        <Typography appearance="heading6" as="p">
          {filename}
        </Typography>
        <Typography as="p">
          {progressStatus}
          {remainingTimeStatus}
        </Typography>
        <div className={styles.progressBar}>
          <ProgressBar
            value={progressPercentage < 100 ? progressPercentage : undefined}
            max={100}
            data-testid="progress-bar"
          />
          <Typography>{progressPercentage < 100 ? `${progressPercentage.toFixed()}%` : <>&nbsp;</>}</Typography>
        </div>
      </WizardPageContent>
      <WizardPageFooter>
        <Button type="button" name="instructions_cad_upload_cancel_upload_click" variant="secondary" onClick={onCancel}>
          {i18n('instructions:create.CAD.cancelUpload')}
        </Button>
      </WizardPageFooter>
    </WizardPageLayout>
  );
}
